import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  encryptRadioBytes,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 64
const MEMORY_SIZE = 0xf250
const CRYPT_END = 0xf000
const CHANNEL_SIZE = 32
const NAME_LENGTH = 12
const POWER_MASK = 0xc0
const NARROW_MASK = 0x02
const AM_MASK = 0x40
const SCAN_MASK = 0x20
const SECONDARY_ID = [0x02, 0x00, 0x02, 0x60, 0x01, 0x03, 0x30, 0x04]
const CRYPT_SETUP = Array.from(
  'SEND ',
  (character) => character.charCodeAt(0),
).concat([
  0x01, 0x01, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
])

type RT900Spec = {
  id: string
  vendor: string
  model: string
  summary: string
  magic: string
  channels: number
  ranges: readonly (readonly [number, number])[]
}

export type RT900Profile = {
  protocol: 'rt900'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 57600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 12
  memorySize: number
  magic: readonly number[]
  ranges: readonly (readonly [number, number])[]
  powerLevels: readonly string[]
}

const RT900_RANGES = [
  [0x0000, 0x7ce0],
  [0x8000, 0x8040],
  [0x9000, 0x9040],
  [0xa000, 0xa140],
  [0xd000, 0xd040],
] as const
const RT910_RANGES = [
  [0x0000, 0x7800],
  [0x8000, 0x8040],
  [0x9000, 0x9040],
  [0xa000, 0xa140],
  [0xd000, 0xd040],
] as const
const RT920_RANGES = [
  [0x0000, 0x7bc0],
  [0x8000, 0x8040],
  [0x9000, 0x9040],
  [0xa000, 0xa140],
  [0xb000, 0xb400],
  [0xc000, 0xc400],
  [0xc800, 0xc8a0],
  [0xd000, 0xd040],
] as const
const GS10B_RANGES = [
  [0x0000, 0x2000],
  [0x8000, 0x8040],
  [0x9000, 0x9040],
  [0xa000, 0xa140],
  [0xd000, 0xd040],
] as const

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

const SPECS: readonly RT900Spec[] = [
  {
    id: 'radtel-rt-900-bt',
    vendor: 'Radtel',
    model: 'RT-900_BT',
    summary: 'Radtel RT-900 Bluetooth wideband mobile.',
    magic: 'PROGRAMBT80U',
    channels: 999,
    ranges: RT900_RANGES,
  },
  {
    id: 'radtel-rt-900',
    vendor: 'Radtel',
    model: 'RT-900',
    summary: 'Radtel RT-900 wideband mobile.',
    magic: 'PROGRAMBT80U',
    channels: 999,
    ranges: RT900_RANGES,
  },
  {
    id: 'radtel-rt-910-bt',
    vendor: 'Radtel',
    model: 'RT-910_BT',
    summary: 'Radtel RT-910 Bluetooth wideband mobile with 15 zones.',
    magic: 'PROGRAMBT80U',
    channels: 960,
    ranges: RT910_RANGES,
  },
  {
    id: 'radtel-rt-910',
    vendor: 'Radtel',
    model: 'RT-910',
    summary: 'Radtel RT-910 wideband mobile with 15 zones.',
    magic: 'PROGRAMBT80U',
    channels: 960,
    ranges: RT910_RANGES,
  },
  {
    id: 'radtel-rt-920',
    vendor: 'Radtel',
    model: 'RT-920',
    summary: 'Radtel RT-920 HF/VHF/UHF mobile with ten zones.',
    magic: 'PROGRAMBT11U',
    channels: 990,
    ranges: RT920_RANGES,
  },
  {
    id: 'radioddity-gs-10b',
    vendor: 'Radioddity',
    model: 'GS-10B',
    summary: 'Radioddity GS-10B 256-channel wideband mobile.',
    magic: 'PROGRAMBT80U',
    channels: 256,
    ranges: GS10B_RANGES,
  },
  {
    id: 'bajeton-bj7800',
    vendor: 'Bajeton',
    model: 'BJ7800',
    summary: 'Bajeton BJ7800 HF/VHF/UHF mobile with 15 zones.',
    magic: 'PROGRAMBT80U',
    channels: 960,
    ranges: RT910_RANGES,
  },
]

export const RT900_PROFILES: readonly RT900Profile[] = SPECS.map((spec) => ({
  protocol: 'rt900',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: [
    'Experimental',
    'Read + write',
    `${spec.channels} channels`,
    'Encrypted clone',
  ],
  baudRate: 57600,
  channelMinimum: 1,
  channelMaximum: spec.channels,
  nameLength: NAME_LENGTH,
  memorySize: MEMORY_SIZE,
  magic: ascii(spec.magic),
  ranges: spec.ranges,
  powerLevels: ['High', 'Middle', 'Low'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'High',
    skip: false,
  }
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value >= 0x0258) {
    return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
  }
  const reversed = value > 0x69
  const index = reversed ? value - 0x6a : value - 1
  return {
    mode: 'dtcs',
    ctcss: null,
    dtcs: DTCS_CODES[index] ?? null,
    polarity: reversed ? 'R' : 'N',
  }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(
      channel.dtcs as (typeof DTCS_CODES)[number],
    )
    if (index < 0) throw new Error('That DCS code is not supported by this radio.')
    value = index + 1 + (channel.dtcsPolarity === 'R' ? 0x69 : 0)
  }
  return [value & 0xff, (value >> 8) & 0xff]
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return (
    tone.mode === 'dtcs' &&
    tone.dtcs === channel.dtcs &&
    tone.polarity === channel.dtcsPolarity
  )
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0xff || value === 0 ? 0x20 : value)),
  ).trim()
}

export function parseRT900Channel(
  image: Uint8Array,
  profile: RT900Profile,
  number: number,
): WebChannel {
  const offset = (number - 1) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw[0] === 0xff) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const txFrequency = raw[4] === 0xff ? null : decodeLbcd(raw.slice(4, 8))
  const txTone = decodeTone(raw.slice(10, 12))
  const rxtone = decodeTone(raw.slice(8, 10))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(20, 32)),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[15] & AM_MASK ? 'AM' : raw[15] & NARROW_MASK ? 'NFM' : 'FM',
    power: profile.powerLevels[(raw[14] >> 6) & 0x03] ?? 'High',
    skip: (raw[15] & SCAN_MASK) === 0,
  }
}

export function parseRT900Channels(
  image: Uint8Array,
  profile: RT900Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseRT900Channel(image, profile, index + 1),
  )
}

export function applyRT900Channel(
  image: Uint8Array,
  profile: RT900Profile,
  channel: WebChannel,
): void {
  if (channel.number < 1 || channel.number > profile.channelMaximum) {
    throw new Error(`Channel must be between 1 and ${profile.channelMaximum}.`)
  }
  const offset = (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  const wasEmpty = raw[0] === 0xff
  if (wasEmpty) {
    raw.fill(0xff)
    raw.fill(0, 0, 16)
  }
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  if (
    !toneMatches(channel, decodeTone(raw.slice(10, 12))) ||
    (!toneMatches(channel, decodeTone(raw.slice(8, 10))) &&
      decodeTone(raw.slice(8, 10)).mode !== 'none')
  ) {
    const tone = encodeTone(channel)
    raw.set(tone, 8)
    raw.set(tone, 10)
  }
  raw[14] =
    (raw[14] & 0x3f) |
    ((Math.max(0, profile.powerLevels.indexOf(channel.power)) & 0x03) << 6)
  raw[15] =
    (raw[15] & ~(NARROW_MASK | AM_MASK | SCAN_MASK)) |
    (channel.mode === 'NFM' || channel.mode === 'AM' ? NARROW_MASK : 0) |
    (channel.mode === 'AM' ? AM_MASK : 0) |
    (channel.skip ? 0 : SCAN_MASK)
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, NAME_LENGTH)
    .padEnd(NAME_LENGTH, '\u00ff')
  for (let index = 0; index < NAME_LENGTH; index += 1) {
    raw[20 + index] = name.charCodeAt(index) & 0xff
  }
}

async function readAck(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  if ((await transport.readExact(1, 3000))[0] !== ACK) throw new Error(message)
}

async function enterProgrammingMode(
  profile: RT900Profile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  await transport.write(Uint8Array.from(profile.magic))
  await readAck(transport, 'The radio refused RT-900 programming mode.')
  await transport.write(new TextEncoder().encode('F'))
  await transport.readExact(8, 3000)
  const secondary = await transport.readExact(8, 3000)
  if (SECONDARY_ID.some((value, index) => secondary[index] !== value)) {
    throw new Error('The connected radio failed RT-900 secondary identification.')
  }
  await transport.write(Uint8Array.from(CRYPT_SETUP))
  await readAck(transport, 'The radio rejected encrypted clone setup.')
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await transport.write(
    Uint8Array.of(0x52, (address >> 8) & 0xff, address & 0xff, BLOCK_SIZE),
  )
  const response = await transport.readExact(4 + BLOCK_SIZE, 5000)
  if (
    response[0] !== 0x52 ||
    response[1] !== ((address >> 8) & 0xff) ||
    response[2] !== (address & 0xff) ||
    response[3] !== BLOCK_SIZE
  ) {
    throw new Error(`Unexpected RT-900 read reply at 0x${address.toString(16)}.`)
  }
  const data = response.slice(4)
  return address < CRYPT_END ? encryptRadioBytes(data, 1) : data
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const payload = address < CRYPT_END ? encryptRadioBytes(data, 1) : data
  const frame = new Uint8Array(4 + BLOCK_SIZE)
  frame.set([
    0x57,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  ])
  frame.set(payload, 4)
  await transport.write(frame)
  await readAck(
    transport,
    `The radio rejected RT-900 write block 0x${address.toString(16)}.`,
  )
}

export async function readRT900RadioWithTransport(
  profile: RT900Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(profile, transport)
  try {
    const total = Math.ceil(MEMORY_SIZE / BLOCK_SIZE)
    const image = new Uint8Array(total * BLOCK_SIZE)
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading RT-900 memory',
      })
    }
    return { image: image.slice(0, MEMORY_SIZE), reportedModel: profile.model }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeRT900RadioWithTransport(
  profile: RT900Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than RT-900 memory.')
  }
  await enterProgrammingMode(profile, transport)
  try {
    const addresses = profile.ranges.flatMap(([start, end]) =>
      Array.from(
        { length: Math.ceil((end - start) / BLOCK_SIZE) },
        (_, index) => start + index * BLOCK_SIZE,
      ),
    )
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      await writeBlock(
        transport,
        address,
        image.slice(address, address + BLOCK_SIZE),
      )
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Writing RT-900 memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport)
  }
}
